import React, { useState, useRef } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
  Warning as WarningIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Restore as RestoreIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { AdvancedSettings as AdvancedSettingsType, PermissionUtils } from './types';
import { themeBackgrounds } from '../../theme/theme';

interface AdvancedSettingsProps {
  advancedSettings: AdvancedSettingsType;
  setAdvancedSettings: React.Dispatch<React.SetStateAction<AdvancedSettingsType>>;
  permissionUtils: PermissionUtils;
  onSave: () => Promise<void>;
  isLoading?: boolean;
}

// Default profanity words and regex patterns
const DEFAULT_PROFANITY_WORDS = [
  // Mild profanity
  'damn', 'hell', 'crap', 'darn', 'bloody',
  // Insults
  'stupid', 'idiot', 'moron', 'jerk', 'fool', 'dumb', 'loser', 'freak', 'weirdo', 'lame',
  // Harassment terms  
  'shut up', 'get lost', 'go away', 'buzz off', 'screw you',
  // Negative descriptors
  'hate', 'suck', 'sucks', 'terrible', 'awful', 'disgusting', 'pathetic',
  // Threats (mild)
  'kill yourself', 'go die', 'drop dead',
  // Common abbreviations
  'wtf', 'omg', 'fml', 'stfu'
];

const DEFAULT_REGEX_PATTERNS = [
  '\\b[f@][u\\*][c\\*][k\\*]\\w*\\b', // f*ck variations with symbols
  '\\b[s\\$][h\\*][i\\*1][t\\*]\\w*\\b', // sh*t variations  
  '\\b[b\\*][i\\*1][t\\*][c\\*][h\\*]\\w*\\b', // b*tch variations
  '\\b[a-z]*kill[a-z]*\\b', // kill variations
  '\\b[a-z]*die[a-z]*\\b', // die variations
  '\\b[a-z]*hate[a-z]*\\b', // hate variations
  '\\b\\w*[4@]ss\\w*\\b' // ass variations with numbers/symbols
];

const AdvancedSettings: React.FC<AdvancedSettingsProps> = ({
  advancedSettings,
  setAdvancedSettings,
  permissionUtils,
  onSave,
  isLoading = false
}) => {
  const { canEditSection } = permissionUtils;
  const [newRegexPattern, setNewRegexPattern] = useState('');
  const [newWord, setNewWord] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAdvancedChange = (field: keyof AdvancedSettingsType) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setAdvancedSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.match(/\.(csv|txt)$/i)) {
      alert('Please select a CSV or TXT file.');
      return;
    }

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size too large. Please select a file smaller than 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        let words: string[] = [];

        // Enhanced parsing for both CSV and TXT files
        // Handle both comma-separated and line-separated values in both formats
        if (file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
          // Split by commas, newlines, semicolons, and tabs
          words = content
            .split(/[,;\n\r\t]/)
            .map(word => word.trim())
            .filter(word => word.length > 0)
            .filter(word => word.length <= 50) // Reasonable word length limit
            .map(word => word.toLowerCase()); // Convert to lowercase for consistency
        }

        if (words.length === 0) {
          alert('No valid keywords found in the file. Please check the file format.');
          return;
        }

        if (words.length > 3000) {
          alert(`File contains ${words.length} keywords. Only the first 3000 will be imported.`);
          words = words.slice(0, 3000);
        }

        const existingWords = new Set(advancedSettings.profanityWords);
        const newWords = words.filter(word => !existingWords.has(word));
        const duplicatesCount = words.length - newWords.length;

        setAdvancedSettings(prev => ({
          ...prev,
          profanityWords: Array.from(new Set([...prev.profanityWords, ...newWords]))
        }));

        // Show success feedback
        let message = `Successfully imported ${newWords.length} new keywords.`;
        if (duplicatesCount > 0) {
          message += ` ${duplicatesCount} duplicates were skipped.`;
        }
        // alert(message);

      } catch (error) {
        alert('Error reading file. Please check the file format and try again.');
      }
    };

    reader.onerror = () => {
      alert('Error reading file. Please try again.');
    };

    reader.readAsText(file);

    // Clear the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateWildcardPattern = (word: string): { isValid: boolean; message?: string } => {
    // Check for wildcard patterns
    const startsWithAsterisk = word.startsWith('*');
    const endsWithAsterisk = word.endsWith('*');

    if (startsWithAsterisk && endsWithAsterisk && word.length <= 2) {
      return { isValid: false, message: 'Wildcard pattern cannot be just asterisks' };
    }

    if (startsWithAsterisk && word.length < 2) {
      return { isValid: false, message: 'Wildcard pattern must have content after *' };
    }

    if (endsWithAsterisk && word.length < 2) {
      return { isValid: false, message: 'Wildcard pattern must have content before *' };
    }

    // Valid patterns: *word, word*, *word*, or regular word
    return { isValid: true };
  };

  const handleProfanityWordsChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputText = event.target.value;

    // Parse text and convert to array of words
    const words = inputText
      .split(/[\n\r,;]/) // Split by newlines, returns, commas, and semicolons
      .map(word => word.trim())
      .filter(word => word.length > 0)
      .map(word => word.toLowerCase()); // Convert to lowercase for consistency

    // Remove duplicates while preserving order
    const uniqueWords = Array.from(new Set(words));

    setAdvancedSettings(prev => ({
      ...prev,
      profanityWords: uniqueWords
    }));
  };

  const removeProfanityWord = (wordToRemove: string) => {
    setAdvancedSettings(prev => ({
      ...prev,
      profanityWords: prev.profanityWords.filter(word => word !== wordToRemove)
    }));
  };

  const addSingleWord = (word: string) => {
    const trimmedWord = word.trim().toLowerCase();
    if (trimmedWord && !advancedSettings.profanityWords.includes(trimmedWord)) {
      const validation = validateWildcardPattern(trimmedWord);
      if (!validation.isValid) {
        alert(validation.message);
        return false;
      }

      setAdvancedSettings(prev => ({
        ...prev,
        profanityWords: [...prev.profanityWords, trimmedWord]
      }));
      return true;
    }
    return false;
  };

  const handleAddMultipleWords = () => {
    if (!newWord.trim()) return;

    // Split by commas and process multiple words
    const words = newWord
      .split(/[,;]/) // Split by commas and semicolons
      .map(word => word.trim().toLowerCase())
      .filter(word => word.length > 0);

    let addedCount = 0;
    let invalidWords: string[] = [];
    let duplicateWords: string[] = [];

    for (const word of words) {
      if (advancedSettings.profanityWords.includes(word)) {
        duplicateWords.push(word);
        continue;
      }

      const validation = validateWildcardPattern(word);
      if (!validation.isValid) {
        invalidWords.push(`${word} (${validation.message})`);
        continue;
      }

      setAdvancedSettings(prev => ({
        ...prev,
        profanityWords: [...prev.profanityWords, word]
      }));
      addedCount++;
    }

    // Show feedback to user
    let message = '';
    if (addedCount > 0) {
      message += `Added ${addedCount} keyword${addedCount > 1 ? 's' : ''}.`;
    }
    if (duplicateWords.length > 0) {
      message += ` ${duplicateWords.length} duplicate${duplicateWords.length > 1 ? 's' : ''} skipped.`;
    }
    if (invalidWords.length > 0) {
      message += ` ${invalidWords.length} invalid pattern${invalidWords.length > 1 ? 's' : ''}: ${invalidWords.join(', ')}`;
    }

    if (message) {
      console.log(message); // You could replace this with a toast notification
    }

    if (addedCount > 0 || words.length === 1) {
      setNewWord(''); // Clear input if any words were added or if it was a single word attempt
    }
  };

  const addRegexPattern = () => {
    if (newRegexPattern.trim() && advancedSettings.regexPatterns.length < 10) {
      try {
        // Validate regex
        new RegExp(newRegexPattern.trim());
        setAdvancedSettings(prev => ({
          ...prev,
          regexPatterns: [...prev.regexPatterns, newRegexPattern.trim()]
        }));
        setNewRegexPattern('');
      } catch (error) {
        alert('Invalid regex pattern. Please check your syntax.');
      }
    }
  };

  const removeRegexPattern = (index: number) => {
    setAdvancedSettings(prev => ({
      ...prev,
      regexPatterns: prev.regexPatterns.filter((_, i) => i !== index)
    }));
  };



  const loadDefaultContent = () => {
    const currentWordsCount = advancedSettings.profanityWords.length;
    const currentPatternsCount = advancedSettings.regexPatterns.length;

    setAdvancedSettings(prev => {
      const newWords = Array.from(new Set([...prev.profanityWords, ...DEFAULT_PROFANITY_WORDS]));
      const newPatterns = Array.from(new Set([...prev.regexPatterns, ...DEFAULT_REGEX_PATTERNS])).slice(0, 10);

      return {
        ...prev,
        profanityFilterEnabled: true, // Enable filter when loading defaults
        profanityWords: newWords,
        regexPatterns: newPatterns
      };
    });

    // Show feedback about what was loaded
    const wordsAdded = Array.from(new Set([...advancedSettings.profanityWords, ...DEFAULT_PROFANITY_WORDS])).length - currentWordsCount;
    const patternsAdded = Math.min(Array.from(new Set([...advancedSettings.regexPatterns, ...DEFAULT_REGEX_PATTERNS])).length, 10) - currentPatternsCount;

    // You could add a toast notification here if needed
    console.log(`Default content loaded: ${wordsAdded} new words, ${patternsAdded} new regex patterns`);
  };

  const clearAllKeywords = () => {
    setAdvancedSettings(prev => ({
      ...prev,
      profanityWords: []
    }));
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <WarningIcon sx={{ mr: 1, color: 'warning.main' }} />
          <Typography variant="h6">Profanity Filter Settings</Typography>
          {!canEditSection('advanced') && (
            <Chip
              label="Read Only"
              color="default"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Configure profanity filter settings to automatically detect and filter inappropriate content in your application.
            Make sure to save your changes after updating the settings.
          </Typography>
        </Alert>

        {/* Profanity Filter Section */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={advancedSettings.profanityFilterEnabled}
                onChange={handleAdvancedChange('profanityFilterEnabled')}
                disabled={!canEditSection('advanced')}
              />
            }
            label="Enable Profanity Filter"
            sx={{ mb: 2 }}
          />

          {advancedSettings.profanityFilterEnabled && (
            <Grid2 container spacing={3}>
              {/* File Upload and Default Content Section */}
              <Grid2 size={{ xs: 12 }}>
                <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Quick Setup Options
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
                    <input
                      type="file"
                      accept=".txt,.csv"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      ref={fileInputRef}
                      disabled={!canEditSection('advanced')}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={!canEditSection('advanced')}
                    >
                      Upload File
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<RestoreIcon />}
                      onClick={loadDefaultContent}
                      disabled={!canEditSection('advanced')}
                      sx={{
                        fontWeight: 'bold',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: 3
                        }
                      }}
                    >
                      Load Default Content
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<ClearIcon />}
                      onClick={clearAllKeywords}
                      disabled={!canEditSection('advanced') || advancedSettings.profanityWords.length === 0}
                      sx={{
                        fontWeight: 'bold',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: 3
                        }
                      }}
                    >
                      Clear All Keywords
                    </Button>
                  </Box>
                  <Typography variant="caption" sx={{ mt: 2, color: 'text.secondary', display: 'block' }}>
                    • Upload supports .txt and .csv files (comma/line/semicolon/tab separated). Files are limited to 5MB and 500 keywords maximum. Load defaults includes {DEFAULT_PROFANITY_WORDS.length} common profanity words and {DEFAULT_REGEX_PATTERNS.length} regex patterns. All content will be merged with existing words (duplicates automatically removed)
                  </Typography>
                </Box>
              </Grid2>

              {/* Add Single Word Section */}
              <Grid2 size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    label="Add Keyword(s)"
                    value={newWord}
                    onChange={(e) => setNewWord(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddMultipleWords();
                      }
                    }}
                    placeholder="Enter keyword(s) separated by commas (use * for wildcards)"
                    disabled={!canEditSection('advanced')}
                    variant="outlined"
                    size="small"
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddMultipleWords}
                    disabled={!canEditSection('advanced') || !newWord.trim()}
                    startIcon={<AddIcon />}
                  >
                    Add
                  </Button>
                </Box>
              </Grid2>

              {/* Keywords Chips Display */}
              {advancedSettings.profanityWords.length > 0 && (
                <Grid2 size={{ xs: 12 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Current Keywords ({advancedSettings.profanityWords.length}):
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 200,
                      overflow: 'auto',
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: 1,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5
                    }}
                  >
                    {advancedSettings.profanityWords.map((word, index) => (
                      <Chip
                        key={`${word}-${index}`}
                        label={word}
                        onDelete={canEditSection('advanced') ? () => removeProfanityWord(word) : undefined}
                        color={word.includes('*') ? 'secondary' : 'default'}
                        variant={word.includes('*') ? 'filled' : 'outlined'}
                        size="small"
                        sx={{
                          fontFamily: word.includes('*') ? 'monospace' : 'inherit',
                          fontWeight: word.includes('*') ? 'bold' : 'normal'
                        }}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                    Wildcard patterns (*) are highlighted in color. Click the × to remove individual keywords.
                  </Typography>
                </Grid2>
              )}

              {/* Regex Patterns Section */}
              <Grid2 size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Regex Patterns (Max 10)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="New Regex Pattern"
                    value={newRegexPattern}
                    onChange={(e) => setNewRegexPattern(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addRegexPattern();
                      }
                    }}
                    placeholder="Enter regex pattern (e.g., \\b[a-z]*bad[a-z]*\\b)"
                    disabled={!canEditSection('advanced') || advancedSettings.regexPatterns.length >= 10}
                    variant="outlined"
                    size="small"
                  />
                  <Button
                    variant="contained"
                    onClick={addRegexPattern}
                    disabled={!canEditSection('advanced') || !newRegexPattern.trim() || advancedSettings.regexPatterns.length >= 10}
                    startIcon={<AddIcon />}
                  >
                    Add
                  </Button>
                </Box>

                {advancedSettings.regexPatterns.length > 0 && (
                  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
                    <List dense>
                      {advancedSettings.regexPatterns.map((pattern, index) => (
                        <ListItem
                          key={index}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => removeRegexPattern(index)}
                              disabled={!canEditSection('advanced')}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={pattern}
                            secondary={`Pattern ${index + 1}`}
                            sx={{ fontFamily: 'monospace' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                  {advancedSettings.regexPatterns.length}/10 patterns configured
                </Typography>
              </Grid2>
            </Grid2>
          )}
        </Box>

        {/* Save Button */}
        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {advancedSettings.profanityFilterEnabled ? (
              <>
                Filter enabled • {advancedSettings.profanityWords.length} words • {advancedSettings.regexPatterns.length} regex patterns
              </>
            ) : (
              'Profanity filter is currently disabled'
            )}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            disabled={
              !canEditSection('advanced') ||
              isLoading
            }
            startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            size="large"
            sx={{
              mr: 1,
              background: themeBackgrounds.default,
              color: '#ffffff',
              '&:hover': {
                background: themeBackgrounds.default,
                opacity: 0.9,
              },
            }}
          >
            {isLoading ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AdvancedSettings;